import { CustomerSubscriptionModel, SubscriptionProductCodes } from '@rightship-tech/common-ui/models/rightstore/rightstore.models';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from '../../../../../../models/basics/basics.models';
import { AddCustomerVesselsRequest as AddCustomerVesselsRequest, AddCustomerVesselsResponse as AddCustomerVesselsResponse } from '../../../../../../models/vessels/customer-vessel.models';
import { NotificationClientService, NotificationType } from '../../../../../../services/basics/notification-client.service';
import { CustomerVesselService } from '../../../../../../services/vessels/customer-vessels.service';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { autoinject } from "aurelia-dependency-injection";
import { CustomValidationRenderer } from '../../../../../../client-validation-system/custom-validation-renderer';
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { computedFrom } from "aurelia-binding";
import { Reflection } from '@rightship-tech/common-ui/helpers/reflection';
import { PaginatedDataGridBase } from "@rightship-tech/common-ui/components/comp-data-grid/paginated-data-grid.base";
import { DataType, SourceType } from '@rightship-tech/common-ui/models/queries.models';
import { DataGridColumn } from '@rightship-tech/common-ui/components/comp-data-grid/comp-data-grid.models';
import { ManageVesselService } from '../../../../../../services/vessels/manage-vessel.service';
import { SubscriptionHelper } from '../../helpers/subscription-helper';

@autoinject
export class AddVesselToSubscriptionDialog extends PaginatedDataGridBase {

    private _onSaved: () => void;
    private _validator: ValidationController;
    private _subscriptionProductCode: string;

    public confirmation: boolean;
    public entityTypesConfigurationModel: EntityTypeConfigurationModel[];
    public selectedVessels: any[] = [];

    public customerId: number;
    public customerSubscription: CustomerSubscriptionModel;
    public infoBottom: string = "";
    public excelUpload: boolean = false;
    public file: File[] = [];
    public uploadVesselCount: number;
    public uploadVesselErrorCount: number;
    public notFoundVesselString: string;
    public isLoading: boolean = false;

    constructor(private dialogClientService: DialogClientService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, private customerVesselService: CustomerVesselService,
        private notificationClientService: NotificationClientService, validationControllerFactory: ValidationControllerFactory,
        private validationsClientService: ValidationsClientService, private vesselService: ManageVesselService, private subscriptionHelper: SubscriptionHelper) {
        super("Vessels");
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());    
    }

    activate(model: { onSaved: () => void, customerId: number, customerSubscription: CustomerSubscriptionModel, subscriptionProductCode: string }) {
        this._onSaved = model.onSaved;
        this.customerId = model.customerId;
        this.customerSubscription = model.customerSubscription;
        this._subscriptionProductCode = model.subscriptionProductCode;
        this.entityTypesConfigurationModel = this._subscriptionProductCode == SubscriptionProductCodes.informationServices
            ? this.entityTypeConfigurationHelper.getConfigurationByKey(["AllVesselSearch"]).filter(x => x.key == "AllVesselSearch")
            : this.entityTypeConfigurationHelper.getConfigurationByKey(["AllCompanySearch", "AllVesselSearch"]);
    }

    @computedFrom(Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z._subscriptionProductCode))
    get isInformationServiceSubscription() {
        return this._subscriptionProductCode == SubscriptionProductCodes.informationServices;
    }

    @computedFrom(Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z.customerSubscription),
    Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z.selectedVessels))
    get shouldDiableSelectVessel() {
        if (this.customerSubscription) {
            const amountCanAdd = this.customerSubscription.maxVesselsCount - this.customerSubscription.currentVesselsCount;
            const shouldDiable = this.selectedVessels.length >= amountCanAdd;
            this.infoBottom = shouldDiable ? `You can only add ${amountCanAdd} more vessel(s)`: "";
            return shouldDiable;
        }
        return false;
    }

    @computedFrom(Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z.customerSubscription),
    Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z.selectedVessels),
    Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z.selectedVessels.length))
    get isSelectedVesselsGreaterThanMaxCount() {
        return this.selectedVessels.length > this.customerSubscription.maxVesselsCount - this.customerSubscription.currentVesselsCount;
    }

    @computedFrom(Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z._subscriptionProductCode),
    Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z.customerId))
    get searchBag() {
        return this._subscriptionProductCode == SubscriptionProductCodes.informationServices ? undefined : `my-vessels-pscriskiq-${this.customerId}`;
    }

    @computedFrom(Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z._subscriptionProductCode))
    get searchLabel() {
        return this._subscriptionProductCode == SubscriptionProductCodes.informationServices ? 'Please search and select a vessel you would like to add' : '';
    }

    @computedFrom(Reflection.getPropertyPath<AddVesselToSubscriptionDialog>(z => z._subscriptionProductCode))
    get searchPlaceholder() {
        return this._subscriptionProductCode == SubscriptionProductCodes.informationServices ? 'Search for a vessel and add it to your list.' : 'Search by DOC / Vessel name / IMO';
    }

    async save() {
        this.setRules();
        try {
            const validationResult = await this._validator.validate();
            if (validationResult.valid) {
                await this.customerVesselService.addCustomerVessels({
                    vesselIds: this.selectedVessels.map(x => x.id),
                    customerId: this.customerId,
                    productCode: this.customerSubscription.productCode
                } as AddCustomerVesselsRequest).then((response: AddCustomerVesselsResponse) => {
                    if (response.validationResult && response.validationResult.hasError) 
                    { 
                        this.validationsClientService.showValidationErrors(response.validationResult);
                    } else {
                        this.dialogClientService.hide();
                        this.notificationClientService.show("Vessel(s) added successfully!", "", NotificationType.Success);
                        this._onSaved();
                    }
                });
            }
        } catch (e) {
            console.error("Failed to change customer vessels", e);
        }
    }

    cancel() {
        this.dialogClientService.hide();
    }

    vesselSelected(index: number, event: any) {
        if (!event.detail.selectedEntity || !event.detail.selectedEntityUri)
            return;

        this.initVesselCount();

        if (event.detail.selectedEntityUri.toLowerCase() == 'allvesselsearch') {
            const vesselId: number = event.detail.selectedEntity.id;
            const findIndex = this.selectedVessels.findIndex(x => x.id === vesselId);
    
            if (findIndex >= 0) {
                this.notificationClientService.show("This vessel has already been added", "", NotificationType.Error);
                this.isLoading = false;
                return;
            }
    
            this.customerVesselService.doesCustomerHaveAccessToVessel({
                vesselId: vesselId,
                customerId: this.customerId
            }).then((canAdd: boolean) => {
                if (!canAdd && this._subscriptionProductCode == SubscriptionProductCodes.PSCRiskIQInApp) {
                    this.notificationClientService.show("The selected vessel is not subscribed to Information Services and cannot be added to PSC RiskIQ. Please subscribe to Information Services first and try again.", "", NotificationType.Error);
                    this.isLoading = false;
                    return;
                }
                
                this.selectedVessels.push(event.detail.selectedEntity);
                this.setItemSource();
            }).finally(() => this.isLoading = false);
        }

        if (event.detail.selectedEntityUri.toLowerCase() == 'companysearch') {
            const companyId: number = event.detail.selectedEntity.id;
            this.vesselService.getVesselsByCompanyId(companyId).then((response) => {
                const newVessels = response.vessels.filter(vessel => 
                    !this.selectedVessels.some(selectedVessel => selectedVessel.id === vessel.id)
                );
                this.selectedVessels = [...this.selectedVessels, ...newVessels];
                this.setItemSource();
            }).finally(() => this.isLoading = false);
        }
    }

    removeSelectedVessel(item: any) {
        this.selectedVessels.splice(this.selectedVessels.indexOf(item), 1);
        this.setItemSource();
    }

    setRules() {
        ValidationRules
            .ensure<AddVesselToSubscriptionDialog, boolean>(x => x.confirmation)
            .required()
            .withMessage("Confirmation is required")
            .on(this);

        this._validator.reset();
        this._validator.addObject(this);
    }

    setItemSource() {
        // Use spread operator to trigger aurelia binding
        this.selectedVessels = [...this.selectedVessels];
        this.itemSource = {
            source: this.selectedVessels,
            sourceType: SourceType.Collection,
            columns: this.getColumns()
        };
    }

    getColumns(): DataGridColumn[] {
        return [
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                name: "index",
                value: '<b class="index">${getIndex(item)}.</b>',
                parameter: {
                    item: null,
                    getIndex: (item: any) => this.selectedVessels.indexOf(item) + 1
                },
                title: "",
                sortOrder: 1,
                columnOrder: 1,
                instancePerRow: true,
                width: "50px"
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                name: "name",
                value: `
                    <div class="details">
                        <span class="name">\${item.name}</span>
                        <p class="info">
                            <span class="prop" if.bind="item.internationalMaritimeOrganizationNumber">IMO \${item.internationalMaritimeOrganizationNumber}</span>
                            <span class="prop" if.bind="item.vehicleIdentificationNumber">VIN \${item.vehicleIdentificationNumber}</span>
                            <span class="prop" if.bind="item.europeanNumberOfIdentification">ENI \${item.europeanNumberOfIdentification}</span>
                            <span class="prop" if.bind="item.maritimeMobileServiceIdentityNumber">MMSI \${item.maritimeMobileServiceIdentityNumber}</span>
                            <span class="prop" if.bind="item.deadweightTonnage">DWT \${item.deadweightTonnage | numberFormat:2}</span>
                            <span class="prop" if.bind="item.vesselTypeValue">\${item.vesselTypeValue}</span>
                        </p>
                    </div>`,
                parameter: {
                    item: null,
                },
                title: "",
                sortOrder: 2,
                columnOrder: 2,
                instancePerRow: true,
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                name: "remove",
                value: `<comp-icon class="icon-remove" icon="bin" click.trigger="removeSelectedVessel(item)"></comp-icon>`,
                parameter: {
                    item: null,
                    removeSelectedVessel: (item: any) => this.removeSelectedVessel(item)
                },
                title: "",
                sortOrder: 3,
                columnOrder: 3,
                instancePerRow: true,
                width: "50px"
            }
        ];
    }

    onFileChanged(event) {
        this.initVesselCount();

        this.vesselService.extractVesselsFromFile({ file: event.detail.files[event.detail.files.length - 1], customerId: this.customerId }).then((response) => {
            if (response.validationResult && response.validationResult.hasError) {
                this.notificationClientService.show("Error", response.validationResult.errors[0].message, NotificationType.Error);
            } else {
                this.selectedVessels = response.vessels;
                this.uploadVesselCount = response.vessels.length;
                this.uploadVesselErrorCount = response.notFoundVesselImos.length;
                this.notFoundVesselString = response.notFoundVesselImos.join(', ');
                this.setItemSource();
            }
        }).finally(() => this.isLoading = false);
    }

    initVesselCount() {
        this.isLoading = true;
        this.uploadVesselCount = 0;
        this.uploadVesselErrorCount = 0;
        this.notFoundVesselString = '';
    }

    upgradeSubscription() {
        const url: string = this.subscriptionHelper.getUpgradeSubscriptionUrl();
        window.open(url, "_blank");
    }
}